import type { FC } from "react";
import React, { useEffect, useRef } from "react";
import {
  usePersistedSessionState,
  useTracking,
} from "@edreamsodigeo/frontend-core";

import type {
  DualPriceContainerWrapperProps,
  HandleOnContinueWithFlightProps,
} from "../contract";
import { useTrackingContext } from "./DualPriceTrackingProvider";
import usePersistedFlightSelectionData from "~/clientState/sessionStorage/persistedFlightSelectionData";
import type { Money as MoneyType } from "~/generated/graphql";
import { FeeTypeId } from "~/components/engage/EqualProminenceInResultsMobile/contract";

export const DualPriceWrapper: FC<DualPriceContainerWrapperProps> = ({
  offer,
  discountedPricePerPassenger,
  nonDiscountedPricePerPassenger,
  onContinueWithFlight,
  isVisible,
  fromResults = false,
  Template,
}) => {
  const { trackEvent } = useTracking();
  const { trackOnContinueButtonClick, trackingCategory } = useTrackingContext();
  const [prevFlightSelectionDataState, setFlightSelectionData] =
    usePersistedFlightSelectionData();
  const [_, setSubscriptionSelectedOnContinue] = usePersistedSessionState(
    "subscriptionSelectedOnContinue",
    "standard",
  );

  /*
   * The values will be rounded by RawMoney component
   * in FareCard component, so we need to round them here
   * to calculate the savings correctly.
   * This is because the RawMoney component uses Math.ceil
   */
  const savings: MoneyType = {
    amount:
      Math.ceil(nonDiscountedPricePerPassenger.amount) -
      Math.ceil(discountedPricePerPassenger.amount),
    currency: nonDiscountedPricePerPassenger.currency,
  };

  const handleOnContinueWithFlight = ({
    priceWithoutDiscountSelected,
  }: HandleOnContinueWithFlightProps): void => {
    setFlightSelectionData({
      ...prevFlightSelectionDataState,
      data: {
        ...prevFlightSelectionDataState?.data,
        priceWithoutDiscountSelected,
      },
      showQuickAddPrime: priceWithoutDiscountSelected,
    });
    trackOnContinueButtonClick({
      wasSelectedPrimePrice: !priceWithoutDiscountSelected,
    });
    setSubscriptionSelectedOnContinue(
      priceWithoutDiscountSelected ? "standard" : "prime",
    );
    if (fromResults) {
      onContinueWithFlight({
        feeTypeId: priceWithoutDiscountSelected
          ? FeeTypeId.MEMBER_PRICE_POLICY_UNDISCOUNTED
          : FeeTypeId.MEMBER_PRICE_POLICY_DISCOUNTED,
      });
    } else {
      onContinueWithFlight();
    }
  };

  const continueWithoutDiscount = (): void =>
    handleOnContinueWithFlight({ priceWithoutDiscountSelected: true });
  const continueWithDiscount = (): void =>
    handleOnContinueWithFlight({ priceWithoutDiscountSelected: false });

  const dualPriceVisibleTrackEventSent = useRef(false);

  useEffect(() => {
    if (isVisible && !dualPriceVisibleTrackEventSent.current) {
      dualPriceVisibleTrackEventSent.current = true;
      trackEvent({
        category: trackingCategory,
        action: "fare-onload",
        label: "fare-onload:onload_pag:summary",
      });
    }
  }, [trackEvent, isVisible, dualPriceVisibleTrackEventSent, trackingCategory]);

  return (
    <Template
      offer={offer}
      savings={savings}
      discountedPricePerPassenger={discountedPricePerPassenger}
      nonDiscountedPricePerPassenger={nonDiscountedPricePerPassenger}
      onContinueWithDiscount={continueWithDiscount}
      onContinueWithoutDiscount={continueWithoutDiscount}
    />
  );
};
